import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen, Target, Flame, TrendingUp, Zap, History, BarChart2, ArrowRight,
  Play, X, AlertTriangle, CheckCircle2, XCircle, Trophy,
} from "lucide-react";
import { api } from "@/lib/api";
import { EXAM_LABELS, SUBJECT_LABELS, subjectAccent } from "@/lib/pyqMeta";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { DailyChallengeDialog } from "@/components/student/DailyChallengeDialog";
import { cn } from "@/lib/utils";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

const KPI = ({ icon: Icon, label, value, sub, tone, testid }) => {
  const tones = {
    blue: "bg-blue-50 text-blue-600", green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600", purple: "bg-violet-50 text-violet-600",
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
      <span className={cn("mb-3 flex h-10 w-10 items-center justify-center rounded-xl", tones[tone])}>
        <Icon className="h-5 w-5" />
      </span>
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</div>
      <div className="mt-1 font-heading text-3xl font-bold">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
};

const Panel = ({ title, children, action }) => (
  <div className="flex flex-col rounded-2xl border border-border bg-card p-5">
    <div className="mb-4 flex items-center justify-between">
      <h2 className="font-heading text-base font-semibold">{title}</h2>
      {action}
    </div>
    <div className="flex-1">{children}</div>
  </div>
);

const sessionQuery = (filters = {}) => {
  const p = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v && k !== "limit") p.set(k, v); });
  p.set("mode", filters.mode || "UNTIMED");
  return p.toString();
};

export default function StudentDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBanner, setShowBanner] = useState(true);
  const [dcOpen, setDcOpen] = useState(false);

  const loadDashboard = () => api.get("/student/dashboard").then((r) => setData(r.data));

  useEffect(() => {
    loadDashboard().finally(() => setLoading(false));
    api.get("/student/insights").then((r) => setInsights(r.data.insights)).catch(() => {});
  }, []);

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <Skeleton className="h-14 w-full rounded-2xl" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}</div>
        <div className="grid gap-4 lg:grid-cols-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-48 rounded-2xl" />)}</div>
      </div>
    );
  }

  const k = data.kpis;
  const empty = k.attempted === 0;

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="student-dashboard">
      {/* Welcome banner */}
      {showBanner && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 px-5 py-3.5">
          <p className="text-sm font-medium text-slate-700">
            {greeting()}, {data.name?.split(" ")[0] || "there"} 👋 {k.streak > 0 ? `${k.streak}-day streak — keep it up!` : "Ready to practise today?"}
          </p>
          <button onClick={() => setShowBanner(false)} aria-label="Dismiss" className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
        </motion.div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KPI icon={BookOpen} label="Total Attempted" value={k.attempted} tone="blue" testid="kpi-attempted" />
        <KPI icon={Target} label="Overall Accuracy" value={`${k.accuracy}%`} tone="green" testid="kpi-accuracy" />
        <KPI icon={Flame} label="Day Streak" value={k.streak} sub={k.streak === 1 ? "day" : "days"} tone="amber" testid="kpi-streak" />
        <KPI icon={TrendingUp} label="Rank Estimate" value={k.rank?.label || "—"} sub="Estimate" tone="purple" testid="kpi-rank" />
      </div>

      {empty ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-14 text-center" data-testid="dashboard-empty">
          <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600"><Play className="h-7 w-7" /></span>
          <h2 className="font-heading text-xl font-semibold">Let's get you started</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">Attempt your first questions and your streak, accuracy and weak areas will show up here.</p>
          <Button onClick={() => navigate("/student/practice/pyq")} className="mt-5 rounded-xl bg-blue-600 px-6 hover:bg-blue-700">Start practising</Button>
        </div>
      ) : (
        <>
          {/* Three panels */}
          <div className="grid gap-4 lg:grid-cols-3">
            <Panel title="Continue Practicing">
              {data.recentSessions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No sessions yet — start one from Previous Years.</p>
              ) : (
                <div className="space-y-2.5">
                  {data.recentSessions.map((s) => {
                    const f = s.filters || {};
                    const label = [EXAM_LABELS[f.exam], SUBJECT_LABELS[f.subject], f.chapter].filter(Boolean).join(" · ") || "Mixed practice";
                    return (
                      <button key={s.id} onClick={() => navigate(`/student/session?${sessionQuery(f)}`)} data-testid={`resume-${s.id}`}
                        className="flex w-full items-center gap-3 rounded-xl border border-border p-3 text-left transition-colors hover:border-blue-300">
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600"><Play className="h-4 w-4" /></span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{label}</div>
                          <div className="text-xs text-muted-foreground">{s.correct}/{s.total} correct · {s.mode === "TIMED" ? "Timed" : "Untimed"}</div>
                        </div>
                        <span className="text-xs font-semibold text-blue-600">Resume</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </Panel>

            <Panel title="Recent PYQ">
              {data.recentQuestions.length === 0 ? (
                <p className="text-sm text-muted-foreground">Your recent questions will appear here.</p>
              ) : (
                <div className="space-y-2.5">
                  {data.recentQuestions.map((qq, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-xl border border-border p-3">
                      {qq.isCorrect ? <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-500" /> : <XCircle className="h-4 w-4 flex-shrink-0 text-red-500" />}
                      <div className="min-w-0 flex-1">
                        <div className={cn("truncate text-sm font-medium", subjectAccent(qq.subject))}>{SUBJECT_LABELS[qq.subject] || qq.subject}</div>
                        <div className="truncate text-xs text-muted-foreground">{EXAM_LABELS[qq.examType]} {qq.year} · {qq.chapter}</div>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" onClick={() => navigate("/student/practice/pyq")} className="w-full rounded-xl">Browse more PYQ</Button>
                </div>
              )}
            </Panel>

            <Panel title="Weak Chapters">
              {data.weakChapters.length === 0 ? (
                <p className="text-sm text-muted-foreground">No weak areas yet — accuracy is holding up. Keep going!</p>
              ) : (
                <div className="space-y-2.5">
                  {data.weakChapters.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-xl border border-red-100 bg-red-50/50 p-3">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 text-red-500" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{c.chapter}</div>
                        <div className="text-xs text-muted-foreground">{SUBJECT_LABELS[c.subject] || c.subject} · {c.accuracy}%</div>
                      </div>
                      <button onClick={() => navigate(`/student/session?${sessionQuery({ subject: c.subject, chapter: c.chapter })}`)} data-testid={`practice-weak-${i}`} className="rounded-full bg-red-500 px-3 py-1 text-xs font-medium text-white hover:bg-red-600">Practice</button>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>

          {/* Exam countdown */}
          {data.examCountdown?.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-3" data-testid="exam-countdown">
              {data.examCountdown.map((e) => (
                <div key={e.name} className="rounded-2xl border border-border bg-card p-4">
                  <div className="text-sm font-semibold">{e.name}</div>
                  <div className="text-xs text-muted-foreground">{e.date}</div>
                  <div className="mt-1 text-2xl font-bold text-blue-600">{e.daysLeft}<span className="ml-1 text-xs font-normal text-muted-foreground">days left</span></div>
                </div>
              ))}
            </div>
          )}

          <div className="grid gap-4 lg:grid-cols-3">
            {/* Daily challenge */}
            {data.dailyChallenge && (
              <Panel title="Challenge of the Day">
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 dark:bg-amber-950/20" data-testid="daily-challenge">
                  <div className="flex items-center justify-between gap-2">
                    <div className={cn("text-sm font-semibold", subjectAccent(data.dailyChallenge.subject))}>{SUBJECT_LABELS[data.dailyChallenge.subject] || data.dailyChallenge.subject}</div>
                    {data.dailyChallengeStreak > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" data-testid="daily-streak-badge">
                        <Flame className="h-3.5 w-3.5" />{data.dailyChallengeStreak}-day
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{EXAM_LABELS[data.dailyChallenge.examType]} · {data.dailyChallenge.chapter}</div>
                  {data.dailyChallengeBadges?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {data.dailyChallengeBadges.map((b) => (
                        <span key={b} className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                          <Trophy className="h-3 w-3" />{b}-day
                        </span>
                      ))}
                    </div>
                  )}
                  {data.dailyChallengeDone ? (
                    <div className="mt-3 flex items-center justify-center gap-1.5 rounded-xl border border-green-300 bg-green-50 py-2.5 text-sm font-medium text-green-700 dark:bg-green-950/30 dark:text-green-300" data-testid="daily-done">
                      <CheckCircle2 className="h-4 w-4" /> Completed today
                    </div>
                  ) : (
                    <Button data-testid="start-daily" onClick={() => setDcOpen(true)} className="mt-3 w-full rounded-xl bg-amber-500 hover:bg-amber-600">Start today's challenge</Button>
                  )}
                </div>
              </Panel>
            )}

            {/* Quick practice */}
            <Panel title="Quick Practice">
              <div className="grid grid-cols-2 gap-2" data-testid="quick-practice">
                <button onClick={() => navigate("/student/mistakes")} className="rounded-xl border border-border p-3 text-left text-sm hover:border-blue-300">
                  <div className="font-medium">Revise errors</div><div className="text-xs text-muted-foreground">{data.quickPractice?.mistakes || 0} pending</div>
                </button>
                <button onClick={() => navigate("/student/bookmarks")} className="rounded-xl border border-border p-3 text-left text-sm hover:border-blue-300">
                  <div className="font-medium">Bookmarks</div><div className="text-xs text-muted-foreground">{data.quickPractice?.bookmarks || 0} saved</div>
                </button>
                <button onClick={() => navigate("/student/practice/topic")} className="rounded-xl border border-border p-3 text-left text-sm hover:border-blue-300">
                  <div className="font-medium">Weak topics</div><div className="text-xs text-muted-foreground">by chapter</div>
                </button>
                <button onClick={() => navigate("/student/practice/pyq")} className="rounded-xl border border-border p-3 text-left text-sm hover:border-blue-300">
                  <div className="font-medium">Random mix</div><div className="text-xs text-muted-foreground">all subjects</div>
                </button>
              </div>
            </Panel>

            {/* Study streak calendar */}
            <Panel title={`Study Streak`}>
              <div className="grid grid-cols-7 gap-1.5" data-testid="streak-calendar">
                {Array.from({ length: new Date(data.streakCalendar.year, data.streakCalendar.month, 0).getDate() }, (_, i) => {
                  const d = `${data.streakCalendar.year}-${String(data.streakCalendar.month).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`;
                  const on = data.streakCalendar.activeDays.includes(d);
                  return <span key={i} className={cn("flex h-7 items-center justify-center rounded-md text-[10px]", on ? "bg-blue-600 text-white" : "bg-secondary text-muted-foreground")}>{i + 1}</span>;
                })}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">{data.streakCalendar.activeDays.length} active days this month</p>
            </Panel>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {/* Leaderboard */}
            <Panel title="Leaderboard — This Week">
              {(!data.leaderboard || data.leaderboard.length === 0) ? (
                <p className="text-sm text-muted-foreground">Answer questions to climb the leaderboard.</p>
              ) : (
                <div className="space-y-2" data-testid="leaderboard">
                  {data.leaderboard.map((l) => (
                    <div key={l.rank} className={cn("flex items-center gap-3 rounded-lg p-2 text-sm", l.you ? "bg-blue-50 dark:bg-blue-950/30" : "")}>
                      <span className="w-6 text-center font-semibold text-muted-foreground">{l.rank}</span>
                      <span className="flex-1 truncate font-medium">{l.you ? "You" : l.name}</span>
                      <span className="text-xs font-semibold text-blue-600">{l.points} pts</span>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            {/* Bookmarked */}
            <Panel title="Bookmarked Questions">
              {(!data.bookmarksPreview || data.bookmarksPreview.length === 0) ? (
                <p className="text-sm text-muted-foreground">Bookmark questions to review them here.</p>
              ) : (
                <div className="space-y-2" data-testid="bookmarks-preview">
                  {data.bookmarksPreview.map((b) => (
                    <button key={b.id} onClick={() => navigate("/student/bookmarks")} className="flex w-full items-center gap-3 rounded-xl border border-border p-2.5 text-left hover:border-blue-300">
                      <div className="min-w-0 flex-1">
                        <div className={cn("truncate text-sm font-medium", subjectAccent(b.subject))}>{SUBJECT_LABELS[b.subject] || b.subject}</div>
                        <div className="truncate text-xs text-muted-foreground">{EXAM_LABELS[b.examType]} · {b.chapter}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </Panel>

            {/* AI insights */}
            <Panel title="AI Insights">
              {!insights ? (
                <p className="text-sm text-muted-foreground">Analysing your performance…</p>
              ) : (
                <div className="space-y-2.5" data-testid="ai-insights">
                  {insights.map((ins, i) => (
                    <div key={i} className="rounded-xl border border-border p-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-blue-600">{ins.kind}</div>
                      <div className="mt-1 text-sm text-foreground/80">{ins.text}</div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>

          {/* Subject progress */}
          <div className="rounded-2xl border border-border bg-card p-6" data-testid="subject-progress">
            <h2 className="mb-5 font-heading text-base font-semibold">Subject Progress</h2>
            <div className="space-y-4">
              {data.subjectProgress.map((s) => (
                <div key={s.subject} className="flex items-center gap-4">
                  <span className="w-28 flex-shrink-0 text-sm font-medium">{SUBJECT_LABELS[s.subject] || s.subject}</span>
                  <Progress value={s.progress} className="h-2 flex-1" />
                  <span className="w-32 flex-shrink-0 text-right text-xs text-muted-foreground">{s.attempted}/{s.available} attempted</span>
                  <span className={cn("w-14 flex-shrink-0 rounded-full px-2 py-0.5 text-center text-xs font-semibold", s.accuracy >= 75 ? "bg-emerald-50 text-emerald-600" : s.accuracy >= 50 ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-500")}>{s.accuracy}%</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Quick actions */}
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => navigate("/student/practice/ai")} data-testid="qa-ai" className="rounded-xl bg-blue-600 hover:bg-blue-700"><Zap className="mr-1.5 h-4 w-4" /> Generate AI Question</Button>
        <Button variant="outline" onClick={() => navigate("/student/practice/pyq")} data-testid="qa-pyq" className="rounded-xl"><History className="mr-1.5 h-4 w-4" /> Browse PYQ</Button>
        <Button variant="outline" onClick={() => navigate("/student/performance")} data-testid="qa-perf" className="rounded-xl"><BarChart2 className="mr-1.5 h-4 w-4" /> View Performance <ArrowRight className="ml-1 h-4 w-4" /></Button>
      </div>

      {data?.dailyChallenge && (
        <DailyChallengeDialog
          open={dcOpen}
          onOpenChange={setDcOpen}
          questionId={data.dailyChallenge.id}
          onCompleted={loadDashboard}
        />
      )}
    </div>
  );
}
